import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';

export interface CityConsultationFormData {
  // Official Information
  fullName: string;
  title: string;
  department: string;
  email: string;
  phone: string;

  // City Details
  cityName: string;
  state: string;
  population: string;

  // Consultation Preferences
  bestTime: string;
  contactMethod: string;
  challenges: string;
}

export interface CityConsultationFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cityName?: string;
  stateName?: string;
  contactEmail?: string;
  /**
   * Required. Must actually persist the submission. There is deliberately no
   * default: a mock default that returned { success: true } showed residents a
   * fake confirmation on any tenant that forgot to pass a real handler.
   */
  onSubmit: (data: CityConsultationFormData) => Promise<{ success: boolean; error?: string }>;
  validateForm?: (data: CityConsultationFormData) => string | null;
}

/**
 * CityConsultationForm - Parameterized city consultation dialog form
 *
 * Dialog-based form for city officials to schedule consultations.
 * Accepts custom validation and submission handlers.
 *
 * @example
 * ```tsx
 * <CityConsultationForm
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   cityName="Houston"
 *   stateName="Texas"
 *   contactEmail="cities@houston.community"
 *   onSubmit={handleConsultationSubmit}
 * />
 * ```
 */
export function CityConsultationForm({
  open,
  onOpenChange,
  cityName = '',
  stateName = '',
  contactEmail = '',
  onSubmit,
  validateForm,
}: CityConsultationFormProps) {
  const [formData, setFormData] = useState<CityConsultationFormData>({
    fullName: '',
    title: '',
    department: '',
    email: '',
    phone: '',
    cityName: cityName,
    state: stateName,
    population: '',
    bestTime: '',
    contactMethod: '',
    challenges: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const defaultValidation = (data: CityConsultationFormData): string | null => {
    if (!data.fullName.trim()) return 'Full name is required';
    if (!data.title) return 'Job title is required';
    if (!data.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      return 'Valid email address is required';
    }
    if (!data.cityName.trim()) return 'City name is required';
    return null;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    const validator = validateForm || defaultValidation;
    const validationError = validator(formData);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await onSubmit(formData);

      if (response.success) {
        setSubmitted(true);
      } else {
        setError(response.error || 'Submission failed. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Form submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      fullName: '',
      title: '',
      department: '',
      email: '',
      phone: '',
      cityName: cityName,
      state: stateName,
      population: '',
      bestTime: '',
      contactMethod: '',
      challenges: '',
    });
    setSubmitted(false);
    setError(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">
            Schedule City Consultation
          </DialogTitle>
          <DialogDescription className="text-center">
            Discuss how the Community App can generate revenue for your city.
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="py-12 text-center space-y-4">
            <div className="flex justify-center">
              <div className="rounded-full bg-green-100 p-3">
                <CheckCircle2 className="w-12 h-12 text-green-600" />
              </div>
            </div>
            <h3 className="text-xl font-semibold">Request Received!</h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              Thank you for your interest. Our municipal partnership team will review your request
              and contact you shortly to schedule a consultation.
            </p>
            <p className="text-sm text-muted-foreground">
              {/* Questions? Email us at <a href={`mailto:${contactEmail}`} className="text-primary hover:underline font-medium">{contactEmail}</a> */}
            </p>
            <Button onClick={resetForm} className="mt-4">
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6 mt-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-4">
              <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider border-b pb-2">
                Official Information
              </h4>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fullName">Full Name *</Label>
                  <Input
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    placeholder="Jane Doe"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="title">Job Title *</Label>
                  <Select
                    value={formData.title}
                    onValueChange={(value) => handleSelectChange('title', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Title" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Mayor">Mayor</SelectItem>
                      <SelectItem value="City Council Member">City Council Member</SelectItem>
                      <SelectItem value="City Manager">City Manager/Administrator</SelectItem>
                      <SelectItem value="Department Head">Department Head</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="department">Department</Label>
                  <Select
                    value={formData.department}
                    onValueChange={(value) => handleSelectChange('department', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Department" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px] overflow-y-auto scroll-smooth">
                      <SelectItem value="Mayor's Office">Mayor's Office</SelectItem>
                      <SelectItem value="City Manager/Administration">
                        City Manager/Administration
                      </SelectItem>
                      <SelectItem value="Economic Development">Economic Development</SelectItem>
                      <SelectItem value="Finance/Treasury">Finance/Treasury</SelectItem>
                      <SelectItem value="Budget & Management">Budget & Management</SelectItem>
                      <SelectItem value="Innovation & Technology">
                        Innovation & Technology
                      </SelectItem>
                      <SelectItem value="City Planning/Zoning">City Planning/Zoning</SelectItem>
                      <SelectItem value="Public Works">Public Works</SelectItem>
                      <SelectItem value="Community Development">Community Development</SelectItem>
                      <SelectItem value="Parks & Recreation">Parks & Recreation</SelectItem>
                      <SelectItem value="Revenue/Tax Administration">
                        Revenue/Tax Administration
                      </SelectItem>
                      <SelectItem value="City Attorney/Legal">City Attorney/Legal</SelectItem>
                      <SelectItem value="Procurement/Purchasing">Procurement/Purchasing</SelectItem>
                      <SelectItem value="Public Affairs/Communications">
                        Public Affairs/Communications
                      </SelectItem>
                      <SelectItem value="City Council Office">City Council Office</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Official Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="jane.doe@city.gov"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider border-b pb-2">
                City Details
              </h4>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cityName">City Name *</Label>
                  <Input
                    id="cityName"
                    name="cityName"
                    value={formData.cityName}
                    onChange={handleInputChange}
                    placeholder={cityName || 'City Name'}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    placeholder={stateName || 'State'}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="population">Population Size</Label>
                <Select
                  value={formData.population}
                  onValueChange={(value) => handleSelectChange('population', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Population Range" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px] overflow-y-auto">
                    <SelectItem value="Under 5,000">Under 5,000</SelectItem>
                    <SelectItem value="5,000 - 10,000">5,000 - 10,000</SelectItem>
                    <SelectItem value="10,000 - 25,000">10,000 - 25,000</SelectItem>
                    <SelectItem value="25,000 - 50,000">25,000 - 50,000</SelectItem>
                    <SelectItem value="50,000 - 75,000">50,000 - 75,000</SelectItem>
                    <SelectItem value="75,000 - 100,000">75,000 - 100,000</SelectItem>
                    <SelectItem value="100,000 - 150,000">100,000 - 150,000</SelectItem>
                    <SelectItem value="150,000 - 200,000">150,000 - 200,000</SelectItem>
                    <SelectItem value="200,000 - 300,000">200,000 - 300,000</SelectItem>
                    <SelectItem value="300,000 - 500,000">300,000 - 500,000</SelectItem>
                    <SelectItem value="500,000 - 750,000">500,000 - 750,000</SelectItem>
                    <SelectItem value="750,000 - 1,000,000">750,000 - 1,000,000</SelectItem>
                    <SelectItem value="1,000,000 - 2,000,000">1,000,000 - 2,000,000</SelectItem>
                    <SelectItem value="2,000,000 - 5,000,000">2,000,000 - 5,000,000</SelectItem>
                    <SelectItem value="5,000,000 - 10,000,000">5,000,000 - 10,000,000</SelectItem>
                    <SelectItem value="Over 10,000,000">Over 10,000,000</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider border-b pb-2">
                Consultation Preferences
              </h4>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bestTime">Best Time to Call</Label>
                  <Select
                    value={formData.bestTime}
                    onValueChange={(value) => handleSelectChange('bestTime', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Time" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Morning">Morning (8am - 12pm)</SelectItem>
                      <SelectItem value="Afternoon">Afternoon (12pm - 5pm)</SelectItem>
                      <SelectItem value="Evening">Evening (After 5pm)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contactMethod">Preferred Contact Method</Label>
                  <Select
                    value={formData.contactMethod}
                    onValueChange={(value) => handleSelectChange('contactMethod', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Method" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Email">Email</SelectItem>
                      <SelectItem value="Phone">Phone Call</SelectItem>
                      <SelectItem value="Video">Video Conference</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="challenges">Current Challenges / Goals</Label>
                <Textarea
                  id="challenges"
                  name="challenges"
                  value={formData.challenges}
                  onChange={handleInputChange}
                  placeholder="Briefly describe what you're looking to achieve (e.g., revenue generation, community engagement, infrastructure funding)..."
                  className="min-h-[100px]"
                  maxLength={1500}
                />
                <p className="text-xs text-muted-foreground text-right">
                  {formData.challenges.length}/1500 characters
                </p>
              </div>
            </div>

            <div className="pt-4">
              <Button type="submit" className="w-full" size="lg" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting Request...
                  </>
                ) : (
                  'Request Consultation'
                )}
              </Button>
              <p className="text-xs text-center text-muted-foreground mt-4">
                By submitting this form, you agree to be contacted by ToolboxLogic regarding
                municipal partnership opportunities.
              </p>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
